import React from 'react';
import styles from './progress_bar.css';

type ProgressBarProps =
  // One of the aria properties should be provided to ensure the progress bar is accessible.
  (
    | {
        ariaLabel: string;
      }
    | {
        ariaDescribedBy: string;
      }
    | {
        arialLabelledBy: string;
      }
  ) & {
    ariaLabel?: string;
    ariaDescribedBy?: string;
    ariaLabelledBy?: string;
    /** First step is 0 and last step equasl `totalSteps`. */
    currentStep: number;
    totalSteps: number;
    /** Progress bars can be used to provide visual feedback that indicates that a task is in progress.
     * To help with that you can set the expected time _in milliseconds_ for the "step" to be completed
     * and the animation will give the impression that "something is happening".
     * Defaults to 0, which means no animation when changing the `currentStep`. */
    currentStepDurationMs?: number;
  };

function useLastStep(currentStep: number) {
  const lastStep = React.useRef(0);
  React.useEffect(() => {
    lastStep.current = currentStep;
  }, [currentStep]);
  return lastStep.current;
}

export function ProgressBar({
  ariaLabel,
  ariaDescribedBy,
  ariaLabelledBy,
  currentStep,
  totalSteps,
  currentStepDurationMs = 0,
}: ProgressBarProps) {
  const translateX = Math.max(
    -100,
    ((totalSteps - currentStep) / totalSteps) * -100,
  );
  // `lastStep` is used to know if we're going backwards. In that case, we want a quicker animation.
  const lastStep = useLastStep(currentStep);
  const transitionDuration =
    currentStep < lastStep
      ? Math.min(currentStepDurationMs, 100)
      : currentStepDurationMs;

  return (
    <div
      role="progressbar"
      aria-valuemax={totalSteps}
      aria-valuemin={0}
      aria-valuenow={currentStep}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-labelledby={ariaLabelledBy}
      aria-valuetext={
        currentStep === 0 ? 'idle' : `step ${currentStep} of ${totalSteps}`
      }
      className={styles.progressBar}
    >
      <div
        aria-hidden={true}
        style={{
          transform: `translateX(${translateX}%)`,
          transitionDuration: `${transitionDuration}ms`,
        }}
        className={styles.progressBarLine}
      />
    </div>
  );
}
